import { ADMIN_API_PREFIX, fillPath, route } from "./routes.js";
import { createRoleRequestSchema } from "./roles.js";
import { CREATE_MACHINE_TRUST_POLICY_ROUTE, UPDATE_MACHINE_TRUST_POLICY_ROUTE } from "./machine-trust-policies.js";
import { z } from "zod";
import { EXTERNAL_PRINCIPAL_REFUSED_ROLE_SLUGS, externalIssuerSchema, issuerNamesPrincipal, machineEnrollRequestSchema, machineEnrollResponseSchema, principalPublicJwkSchema } from "@zion/sdk-contracts";
//#region src/machine-principals.ts
/**
* `/v1/admin/machine-principals` — the org's `external` machine principals, their registered
* public keys and their enroll tokens (feature 2026-10-01-dp-oidc-federation, D-011, D-012,
* D-013).
*
* Producer: the WS-5 admin controller under `cloud/dp/core-api/src/admin/machine-principals/`.
* Consumer: the org admin app's client. An `external` principal is a system outside the platform
* that logs in by exchanging an OIDC assertion at `POST /v1/auth/token/federated`
* (`FEDERATED_TOKEN_PATH` in `@zion/sdk-contracts`). It holds no NATS credential, no stream and no
* compute-unit attribute (DF-13); its permissions are the org roles named at create and the roles
* of every group it later joins. It never holds a role of `EXTERNAL_PRINCIPAL_REFUSED_ROLE_SLUGS`,
* nor a role whose permissions reach `EXTERNAL_PRINCIPAL_REFUSED_PERMISSIONS` (R-025).
*
* Every route here addresses `external` principals only. The list returns no other type, and a
* route given the id of an agent, worker, service or human principal answers 404 as for an
* unknown id, so this family can neither revoke a compute unit nor reveal one exists. The
* operator's routes under `internal/v1/machine-principals` keep managing the other types.
*
* A principal signs its own assertions with a key whose public half is registered here, either by
* the admin ({@link REGISTER_MACHINE_PRINCIPAL_KEY_ROUTE}) or by the machine itself with a one-use
* enroll token ({@link CREATE_MACHINE_ENROLL_TOKEN_ROUTE}, then `MACHINE_ENROLL_PATH` in
* `@zion/sdk-contracts`). A principal holds at most one active registered key; rotate by revoking
* the active key, then registering the next.
*
* Permissions (R-015): the class carries `settings:identity:manage`; the two list handlers narrow
* to `settings:identity:read`. `NoDelegatedPrincipalGuard` sits on the class, so a token carrying
* `scp` is refused at every route here. Every `{id}` route resolves the principal inside the
* caller's org under RLS, so another org's principal is a 404. Key registration and enroll-token
* mint are credential-granting writes, and carry that guard set with the trust-policy create and
* re-enable. The no-escalation check below is the set's fourth guard (R-017).
*
* No escalation through a principal (R-017). `settings:identity:manage` is a grantable catalog
* permission, so its holder may hold far fewer permissions than a principal an admin created.
* Every route that names roles for a principal or hands the caller a credential for one answers
* 403 unless the caller could confer the principal's whole effective authority: create over the
* body's `roleSlugs`; key registration and enroll-token mint over the roles the principal holds
* directly, the roles of every group it belongs to, and the resource grants whose subject is the
* principal, one of those groups or one of those roles. {@link MACHINE_PRINCIPAL_ESCALATION_CHECKED_ROUTES}
* defines the check and lists these routes with the two trust-policy routes that carry it;
* {@link machinePrincipalEscalationScope} computes what the check covers.
*
* Audit, one row per write, each inside the write's transaction: create writes the existing
* `machine_principal.enrolled` with the calling admin as actor, human or machine (R-022, R-062), and revoke writes the
* existing `machine_principal.revoked`; a key registration writes `machine_principal.key_registered`
* (DF-12); a key revoke writes `machine_principal.key_revoked` (R-014); an enroll-token mint writes
* `machine_principal.enroll_token_created` (R-011). `machine_principal.enrolled_external` belongs
* to the public enroll route at `MACHINE_ENROLL_PATH`, never to this family (R-022). No row carries
* an enroll token or its hash.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped and cannot red a page. Every request schema is `z.strictObject`, so a key the server
* would reject is refused before the request leaves the client. Shapes that live in
* `@zion/sdk-contracts` (`principalPublicJwkSchema`, `machineEnrollRequestSchema`,
* `machineEnrollResponseSchema`, `EXTERNAL_PRINCIPAL_REFUSED_ROLE_SLUGS`) are imported; the one used
* as a response is rebuilt as `z.object(upstream.shape)` (R-38), and its issuer refine calls the
* upstream predicate `issuerNamesPrincipal`, as the principal row's does (R-029). No schema here
* carries a `.transform()`.
*/
/** The collection path. No parameter, so the OpenAPI form and the request form are the same. */
const ADMIN_MACHINE_PRINCIPALS_PATH = `${ADMIN_API_PREFIX}/machine-principals`;
/** Revoke one principal, in OpenAPI form. Fill it with {@link adminMachinePrincipalRevokePath}. */
const ADMIN_MACHINE_PRINCIPAL_REVOKE_PATH_TEMPLATE = `${ADMIN_MACHINE_PRINCIPALS_PATH}/{id}/revoke`;
/** One principal's registered keys, in OpenAPI form. Fill it with {@link adminMachinePrincipalKeysPath}. */
const ADMIN_MACHINE_PRINCIPAL_KEYS_PATH_TEMPLATE = `${ADMIN_MACHINE_PRINCIPALS_PATH}/{id}/keys`;
/** One registered key, in OpenAPI form. Fill it with {@link adminMachinePrincipalKeyPath}. */
const ADMIN_MACHINE_PRINCIPAL_KEY_PATH_TEMPLATE = `${ADMIN_MACHINE_PRINCIPAL_KEYS_PATH_TEMPLATE}/{kid}`;
/** One principal's enroll tokens, in OpenAPI form. Fill it with {@link adminMachineEnrollTokensPath}. */
const ADMIN_MACHINE_ENROLL_TOKENS_PATH_TEMPLATE = `${ADMIN_MACHINE_PRINCIPALS_PATH}/{id}/enroll-tokens`;
/** The only principal type this family creates and lists (D-013, DF-3). */
const ADMIN_MACHINE_PRINCIPAL_TYPE = "external";
/** The most org roles one create may name. */
const MACHINE_PRINCIPAL_MAX_ROLES = 16;
/**
* The built-in org roles an `external` principal may never hold: `EXTERNAL_PRINCIPAL_REFUSED_ROLE_SLUGS`
* from `@zion/sdk-contracts` (R-012, R-025), under the name this family published in round 1. The
* same frozen array, so the two names never disagree: `owner`, `admin`, `platform-agent-runtime`
* and `platform-machine-enroller`. `admin` holds `settings:identity:manage` by implication, so an
* `external` admin could register keys for every other principal (R-025).
*
* An org owner holds every permission, so `assertAssignmentNoEscalation` alone would admit all
* four; the create body refuses them by name before the request leaves the client, and core-api
* refuses them on the server (WS-5).
*
* A custom role has no fixed slug, so no schema here can refuse it. core-api refuses it by what it
* holds (R-025, R-035): any role whose permissions, expanded through `ORG_PERMISSION_IMPLICATIONS`,
* satisfy `hasPermission(expanded, entry)` for an entry of `EXTERNAL_PRINCIPAL_REFUSED_PERMISSIONS`
* from `@zion/sdk-contracts`. The check is wildcard-aware, so a role holding `settings:*` or
* `settings:identity:*` is refused as well. It refuses such a role at this family's create, with
* a 400, and at every other role write that reaches a principal of type `external`, a role
* inherited through a group included (WS-5 owns those routes' status codes).
* A custom role without those permissions, such as one holding `channels:read` and `audit:read`,
* is accepted.
*/
const MACHINE_PRINCIPAL_REFUSED_ROLE_SLUGS = EXTERNAL_PRINCIPAL_REFUSED_ROLE_SLUGS;
const refusedRoleSlugs = new Set(MACHINE_PRINCIPAL_REFUSED_ROLE_SLUGS);
/** The state of a principal and of a registered key. `revoked` is final. */
const machinePrincipalStatusSchema = z.enum(["active", "revoked"]);
/**
* One `external` principal, as the list, create and revoke routes return it.
*
* - `id`: the `auth.machine_principals` uuid; the `sub` of the tokens the exchange mints for it,
*   and the `subject` a trust policy for its registered key names.
* - `type`: always `external`.
* - `displayName`: the free text the create body carried.
* - `status`: `active`, or `revoked` once {@link REVOKE_MACHINE_PRINCIPAL_ROUTE} ran.
* - `roleSlugs`: the org roles assigned to it directly, sorted. The plane reads them from
*   `auth.principal_roles` through `ctx.principalRoles.listByPrincipalId(id)`, the read
*   `PrincipalPermissionResolver` makes for a machine and the table `MachineIdentityService` and
*   `OrgRoleService` write. The legacy `auth.machine_principal_roles` table is frozen since
*   core-api migration 0024 and holds no role written after it, so a plane that read it would list
*   `[]` for every principal created since. A token minted for the principal also carries the
*   `org_group_roles` of every group it belongs to, which this member does not list; the escalation
*   check reads both ({@link MACHINE_PRINCIPAL_ESCALATION_CHECKED_ROUTES}).
* - `registeredKeyIssuer`: the issuer core-api assigns to this principal's registered keys (R-001),
*   `https://<org host>/machine-principals/<id>`, which the machine's own assertions carry as `iss`
*   and which a trust policy for them names. It is `externalIssuerSchema` from
*   `@zion/sdk-contracts`, the schema the enroll and key-registration responses give the same value
*   with, and a trust policy's `issuer` admits it, so an admin can paste it into a policy.
* - `createdAt`, `revokedAt`: ISO 8601 UTC; `revokedAt` is null while active.
*
* Refused (DF-3): a row whose `registeredKeyIssuer` does not name `id` by `issuerNamesPrincipal`
* from `@zion/sdk-contracts` (R-029), with the issue on `registeredKeyIssuer`: an issuer of another
* principal, and an `id` in upper case, which the lower-case issuer path never ends with. The list
* parse fails whole on such a row, so the admin never copies another principal's issuer into a
* policy.
*/
const adminMachinePrincipalSchema = z.object({
	id: z.uuid(),
	type: z.literal(ADMIN_MACHINE_PRINCIPAL_TYPE),
	displayName: z.string(),
	status: machinePrincipalStatusSchema,
	roleSlugs: z.array(z.string()),
	registeredKeyIssuer: externalIssuerSchema,
	createdAt: z.iso.datetime(),
	revokedAt: z.iso.datetime().nullable()
}).refine((row) => issuerNamesPrincipal(row.registeredKeyIssuer, row.id), {
	message: "registeredKeyIssuer: names another principal than id",
	path: ["registeredKeyIssuer"]
});
/**
* One registered public key of a principal, as the key list and key revoke routes return it.
* Never the JWK's private member, which the register route refuses to accept.
*
* - `kid`: the RFC 7638 SHA-256 thumbprint core-api computed, `machineEnrollResponseSchema`'s
*   `kid` form.
* - `alg`: `EdDSA` for an Ed25519 key, `ES256` for a P-256 key.
* - `status`: `active`, or `revoked` once {@link REVOKE_MACHINE_PRINCIPAL_KEY_ROUTE} ran.
* - `createdAt`, `revokedAt`: ISO 8601 UTC; `revokedAt` is null while active.
*/
const adminMachinePrincipalKeySchema = z.object({
	kid: machineEnrollResponseSchema.shape.kid,
	alg: z.enum(["EdDSA", "ES256"]),
	status: machinePrincipalStatusSchema,
	createdAt: z.iso.datetime(),
	revokedAt: z.iso.datetime().nullable()
});
/**
* `GET /v1/admin/machine-principals`. Every `external` principal of the caller's org, active and
* revoked, unpaged. Status 200. Permission `settings:identity:read`.
*/
const LIST_MACHINE_PRINCIPALS_ROUTE = route("GET", ADMIN_MACHINE_PRINCIPALS_PATH);
/**
* `POST /v1/admin/machine-principals`. Creates one active `external` principal with the named org
* roles, none when `roleSlugs` is `[]` (app-oidc R-027), and no key. Status 201 with the row. 400 on a body
* {@link createMachinePrincipalRequestSchema} refuses, when a slug names no built-in or custom
* role of the org, and when a slug names a custom role whose permissions grant an entry of
* `EXTERNAL_PRINCIPAL_REFUSED_PERMISSIONS` (R-025, see {@link MACHINE_PRINCIPAL_REFUSED_ROLE_SLUGS}).
* Each 400 is decided from the org's role catalog before the escalation check. 403 when the
* caller could not assign one of the roles to a user, the check `assertAssignmentNoEscalation`
* makes for a user assignment, or could not write one of the active resource grants whose subject
* is one of those roles (R-017, R-032). A principal this route creates
* belongs to no group and is the subject of no resource grant, so the body's `roleSlugs` and their
* role-subject grants are its whole effective authority
* ({@link MACHINE_PRINCIPAL_ESCALATION_CHECKED_ROUTES}). Permission
* `settings:identity:manage`. Audit `machine_principal.enrolled`, actor the calling admin, human or machine (R-022, R-062).
*/
const CREATE_MACHINE_PRINCIPAL_ROUTE = route("POST", ADMIN_MACHINE_PRINCIPALS_PATH);
/**
* `POST /v1/admin/machine-principals/{id}/revoke`. No body. Revokes the principal: the exchange
* refuses it from then on, and a token already minted for it runs out within 900 s (D-005). Its
* keys and trust policies stay as rows. Status 200 with the row; revoking a revoked principal
* answers 200 with the row unchanged. 404 for an unknown id, another org's id and a principal that
* is not `external`. Permission `settings:identity:manage`.
*/
const REVOKE_MACHINE_PRINCIPAL_ROUTE = route("POST", ADMIN_MACHINE_PRINCIPAL_REVOKE_PATH_TEMPLATE);
/**
* `GET /v1/admin/machine-principals/{id}/keys`. Every registered key of the principal, active and
* revoked, newest first. Status 200. 404 as for revoke. Permission `settings:identity:read`.
*/
const LIST_MACHINE_PRINCIPAL_KEYS_ROUTE = route("GET", ADMIN_MACHINE_PRINCIPAL_KEYS_PATH_TEMPLATE);
/**
* `POST /v1/admin/machine-principals/{id}/keys`. Registers one public JWK for the principal.
* core-api imports the key, computes its RFC 7638 `kid` and stores it with no KMS version. Status
* 201 with {@link registerMachinePrincipalKeyResponseSchema}. 400 on a body
* {@link registerMachinePrincipalKeyRequestSchema} refuses and on a key that fails to import (an
* EC point off the curve). 404 as for revoke. 403 when the caller could not confer the
* principal's whole effective authority, the check {@link MACHINE_PRINCIPAL_ESCALATION_CHECKED_ROUTES}
* defines (R-017): every role it holds directly, every role of every group it belongs to at any
* depth, and every resource grant whose subject is the principal, one of those groups or one of
* those roles. The check runs once the principal resolves
* and before any write: the caller holds the private half, so a registration hands them a
* credential carrying everything the principal can do. 403 outranks 409, so a caller who fails it
* learns nothing about the principal's keys. 409 when the principal is revoked, when it
* already holds an active registered key, and when the computed `kid` already exists. Permission
* `settings:identity:manage`, `NoDelegatedPrincipalGuard`, the principal resolved inside the
* caller's org under RLS (R-015), and the 403 check above as the fourth guard (R-017). Audit
* `machine_principal.key_registered`.
*/
const REGISTER_MACHINE_PRINCIPAL_KEY_ROUTE = route("POST", ADMIN_MACHINE_PRINCIPAL_KEYS_PATH_TEMPLATE);
/**
* `DELETE /v1/admin/machine-principals/{id}/keys/{kid}`. Revokes one registered key: the exchange
* refuses an assertion signed with it from then on. The row stays. Status 200 with the key row;
* revoking a revoked key answers 200 with the row unchanged. 404 for an unknown principal or
* `kid`, another org's, a principal that is not `external`, and a `kid` of another principal.
* Permission `settings:identity:manage`.
*/
const REVOKE_MACHINE_PRINCIPAL_KEY_ROUTE = route("DELETE", ADMIN_MACHINE_PRINCIPAL_KEY_PATH_TEMPLATE);
/**
* `POST /v1/admin/machine-principals/{id}/enroll-tokens`. No body. Mints one enroll token bound to
* the principal and the org: 32 random bytes, valid for one hour, accepted once, stored as its
* SHA-256 hash (DF-14). Status 201 with {@link createMachineEnrollTokenResponseSchema}, the only
* response that ever carries the raw token. Each call mints an independent token. 404 as for
* revoke. 403 as for {@link REGISTER_MACHINE_PRINCIPAL_KEY_ROUTE}, over the same effective
* authority: the token lets whoever holds it register a key, so a mint hands the caller a
* credential carrying everything the principal can do; no token row is written. 409 when the
* principal is revoked. Permission `settings:identity:manage`, `NoDelegatedPrincipalGuard`, the
* principal resolved inside the caller's org under RLS (R-015), and the 403 check as the fourth
* guard (R-017). Audit `machine_principal.enroll_token_created`.
*/
const CREATE_MACHINE_ENROLL_TOKEN_ROUTE = route("POST", ADMIN_MACHINE_ENROLL_TOKENS_PATH_TEMPLATE);
/** The id schema every builder here validates a principal id with. */
const principalIdParamSchema = z.uuid();
/**
* Fill {@link ADMIN_MACHINE_PRINCIPAL_REVOKE_PATH_TEMPLATE}.
*
* @param principalId the principal uuid, as {@link adminMachinePrincipalSchema} `id` publishes it.
* @throws a `ZodError` when `principalId` is not a uuid.
*/
function adminMachinePrincipalRevokePath(principalId) {
	return fillPath(ADMIN_MACHINE_PRINCIPAL_REVOKE_PATH_TEMPLATE, { id: principalId }, { id: principalIdParamSchema });
}
/**
* Fill {@link ADMIN_MACHINE_PRINCIPAL_KEYS_PATH_TEMPLATE}, the path of the key list and the key
* registration.
*
* @param principalId the principal uuid.
* @throws a `ZodError` when `principalId` is not a uuid.
*/
function adminMachinePrincipalKeysPath(principalId) {
	return fillPath(ADMIN_MACHINE_PRINCIPAL_KEYS_PATH_TEMPLATE, { id: principalId }, { id: principalIdParamSchema });
}
/**
* Fill {@link ADMIN_MACHINE_PRINCIPAL_KEY_PATH_TEMPLATE}.
*
* @param principalId the principal uuid.
* @param kid the key's RFC 7638 thumbprint, as {@link adminMachinePrincipalKeySchema} `kid`
*   publishes it: 43 base64url characters, which travel unencoded.
* @throws a `ZodError` when `principalId` is not a uuid or `kid` is not a thumbprint.
*/
function adminMachinePrincipalKeyPath(principalId, kid) {
	return fillPath(ADMIN_MACHINE_PRINCIPAL_KEY_PATH_TEMPLATE, {
		id: principalId,
		kid
	}, {
		id: principalIdParamSchema,
		kid: machineEnrollResponseSchema.shape.kid
	});
}
/**
* Fill {@link ADMIN_MACHINE_ENROLL_TOKENS_PATH_TEMPLATE}.
*
* @param principalId the principal uuid.
* @throws a `ZodError` when `principalId` is not a uuid.
*/
function adminMachineEnrollTokensPath(principalId) {
	return fillPath(ADMIN_MACHINE_ENROLL_TOKENS_PATH_TEMPLATE, { id: principalId }, { id: principalIdParamSchema });
}
/** `GET /v1/admin/machine-principals` response: a bare array of rows. */
const listMachinePrincipalsResponseSchema = z.array(adminMachinePrincipalSchema);
/**
* `POST /v1/admin/machine-principals` body.
*
* - `type`: the literal `external`, required. Any other type is a 400 (DF-3): the agent, worker,
*   service and human types are enrolled by the operator, never by an org admin.
* - `displayName`: 1 to 256 characters with at least one that is not whitespace.
* - `roleSlugs`: 0 to {@link MACHINE_PRINCIPAL_MAX_ROLES} distinct org role slugs, each the
*   `slug` form of `createRoleRequestSchema` in `roles.ts` (1 to 256 characters), so every role
*   an admin can create can be assigned. Every slug of {@link MACHINE_PRINCIPAL_REFUSED_ROLE_SLUGS}
*   is refused (R-012, R-025): `owner`, `admin` and the two platform roles. `member` and a custom
*   slug parse; the server refuses a custom role by its permissions. `[]` parses (app-oidc ruling
*   R-027): it creates a principal with no role, the one kind of principal that a trust policy
*   bound to an app may name (app-oidc ruling R-018), so an org admin makes it in one step. Such a
*   principal holds no org permission; its tokens carry only what a binding grants. The key is
*   still required: a body with no `roleSlugs` is refused.
*
* Any other key is refused.
*/
const createMachinePrincipalRequestSchema = z.strictObject({
	type: z.literal(ADMIN_MACHINE_PRINCIPAL_TYPE),
	displayName: z.string().min(1).max(256).regex(/\S/, "displayName must not be blank"),
	roleSlugs: z.array(createRoleRequestSchema.shape.slug).max(16).refine((slugs) => new Set(slugs).size === slugs.length, { message: "roleSlugs must not repeat a slug" }).refine((slugs) => !slugs.some((slug) => refusedRoleSlugs.has(slug)), { message: `an external machine principal may not hold an owner-class or platform role: ${MACHINE_PRINCIPAL_REFUSED_ROLE_SLUGS.join(", ")}` })
});
/** `POST /v1/admin/machine-principals` response: the created row, `status: 'active'`. */
const createMachinePrincipalResponseSchema = adminMachinePrincipalSchema;
/** `POST /v1/admin/machine-principals/{id}/revoke` response: the row, `status: 'revoked'`. */
const revokeMachinePrincipalResponseSchema = adminMachinePrincipalSchema;
/** `GET /v1/admin/machine-principals/{id}/keys` response: a bare array of key rows. */
const listMachinePrincipalKeysResponseSchema = z.array(adminMachinePrincipalKeySchema);
/**
* `POST /v1/admin/machine-principals/{id}/keys` body: `{ publicJwk }`, where `publicJwk` is
* `principalPublicJwkSchema` from `@zion/sdk-contracts`: an Ed25519 or P-256 public JWK with no
* private member `d`, no caller-chosen `kid` and no other member. Any other body key is refused.
*/
const registerMachinePrincipalKeyRequestSchema = z.strictObject({ publicJwk: principalPublicJwkSchema });
/**
* `POST /v1/admin/machine-principals/{id}/keys` response: `{ principalId, kid, issuer }`, what the
* machine needs to sign its assertions, the same three members the enroll route answers with.
* `z.object(machineEnrollResponseSchema.shape)`: the owner in `@zion/sdk-contracts` is strict, and
* this rebuild strips a member core-api adds (R-38). A test pins the two key sets equal.
*
* The rebuild drops the owner's refine, so it is restated with the owner's own predicate,
* `issuerNamesPrincipal` from `@zion/sdk-contracts` (R-029): a response whose `issuer` does not name
* `principalId` is refused, with the issue on `issuer` (DF-3), and a client never signs as one
* principal with another's issuer. The two responses accept and refuse the same bodies.
*/
const registerMachinePrincipalKeyResponseSchema = z.object(machineEnrollResponseSchema.shape).refine((response) => issuerNamesPrincipal(response.issuer, response.principalId), {
	message: "issuer: names another principal than principalId",
	path: ["issuer"]
});
/** `DELETE /v1/admin/machine-principals/{id}/keys/{kid}` response: the key row, `status: 'revoked'`. */
const revokeMachinePrincipalKeyResponseSchema = adminMachinePrincipalKeySchema;
/**
* `POST /v1/admin/machine-principals/{id}/enroll-tokens` response.
*
* - `id`: the `auth.machine_enroll_tokens` uuid, for the audit trail.
* - `enrollToken`: the raw token, `machineEnrollRequestSchema`'s `enrollToken` form. Shown once;
*   the caller hands it to the machine before it navigates away.
* - `expiresAt`: ISO 8601 UTC, one hour after the mint.
*/
const createMachineEnrollTokenResponseSchema = z.object({
	id: z.uuid(),
	enrollToken: machineEnrollRequestSchema.shape.enrollToken,
	expiresAt: z.iso.datetime()
});
/** Every route this file declares, for `route-census.ts`. */
const machinePrincipalsRoutes = Object.freeze([
	LIST_MACHINE_PRINCIPALS_ROUTE,
	CREATE_MACHINE_PRINCIPAL_ROUTE,
	REVOKE_MACHINE_PRINCIPAL_ROUTE,
	LIST_MACHINE_PRINCIPAL_KEYS_ROUTE,
	REGISTER_MACHINE_PRINCIPAL_KEY_ROUTE,
	REVOKE_MACHINE_PRINCIPAL_KEY_ROUTE,
	CREATE_MACHINE_ENROLL_TOKEN_ROUTE
]);
/**
* The routes of the two admin families that answer 403 when the caller could not confer a
* principal's effective authority (R-017). Each one names roles for a principal or hands the
* caller a credential that logs in as one, so without the check a holder of
* `settings:identity:manage` could take over a principal an admin created, or one an admin put in
* a powerful group or gave a role that carries grants. The check is the fourth guard of R-015's
* set, beside `settings:identity:manage`, `NoDelegatedPrincipalGuard` and the RLS-bound principal.
*
* The effective authority is what a token minted for the principal carries. The check refuses on
* the first part the caller could not confer:
*
* 1. Every role slug the principal holds directly (`auth.principal_roles`, read through
*    `ctx.principalRoles.listByPrincipalId`, the row's `roleSlugs`; never the frozen
*    `auth.machine_principal_roles`, which holds no role written after core-api migration 0024 and
*    would let this part pass over an empty set) and every `org_group_roles` slug of every group in its flattened membership, the
*    org-bounded, depth-capped set `GroupMemberRepository.flattenedGroupIdsForPrincipal` returns
*    and `MachinePermissionResolver.resolve` mints from. 403 unless the caller could assign each
*    slug to a user, the check `assertAssignmentNoEscalation` makes. A group-inherited `admin` or
*    custom role refuses a caller who passes over the direct roles alone.
* 2. Every active resource grant whose subject is the principal (`principal`, its id), one of
*    those groups (`group`, the group id) or one of the slugs of part 1 (`role`, the slug): all
*    three `subject_kind` values of `RESOURCE_GRANT_SUBJECT_KINDS`. 403 unless the caller could
*    write that grant on its resource, the check `ResourceGrantWriterPolicy.canWrite` makes before
*    a grant write, asked once per grant; the first refusal answers 403 (R-032). A `release_channel:manage` grant to a role the principal holds refuses a
*    caller who could not write it, though assigning the role itself would pass part 1.
*
* {@link machinePrincipalEscalationScope} turns the principal's three subject inputs and the org's
* grant rows into the slugs of part 1 and the grants of part 2, so the plane walks one list of
* each and no subject kind can be left out.
*
* Per route:
*
* - {@link CREATE_MACHINE_PRINCIPAL_ROUTE}: over the body's `roleSlugs` (part 1), and part 2 over
*   the grants whose subject is one of those slugs. A new principal belongs to no group and is the
*   subject of no grant yet.
* - {@link REGISTER_MACHINE_PRINCIPAL_KEY_ROUTE} and {@link CREATE_MACHINE_ENROLL_TOKEN_ROUTE}:
*   over the addressed principal's effective authority.
* - `CREATE_MACHINE_TRUST_POLICY_ROUTE`: over the effective authority of the body's
*   `machinePrincipalId`.
* - `UPDATE_MACHINE_TRUST_POLICY_ROUTE`: over the effective authority of the row's principal, on a
*   body of `{ disabled: false }` only.
*
* Every other write here lowers what a principal can do: {@link REVOKE_MACHINE_PRINCIPAL_ROUTE}
* and {@link REVOKE_MACHINE_PRINCIPAL_KEY_ROUTE}. The check runs once the principal resolves, so
* 404 stays the answer for another org's id, and before any write. Consumer: WS-5's controller
* tests, which bind one 403 arm to each entry, and on the key-register route one arm per part: a
* direct role written through `ctx.principalRoles.insert`, as `MachineIdentityService` and
* `OrgRoleService` write one, so the arm reds a check that reads the frozen table; a role inherited through a nested group, a grant to the principal, a grant to one
* of its groups, a grant to a role it holds directly and a grant to a role it inherits through a
* group, each with every other part conferrable.
*/
const MACHINE_PRINCIPAL_ESCALATION_CHECKED_ROUTES = Object.freeze([
	CREATE_MACHINE_PRINCIPAL_ROUTE,
	REGISTER_MACHINE_PRINCIPAL_KEY_ROUTE,
	CREATE_MACHINE_ENROLL_TOKEN_ROUTE,
	CREATE_MACHINE_TRUST_POLICY_ROUTE,
	UPDATE_MACHINE_TRUST_POLICY_ROUTE
]);
/** The 8-4-4-4-12 hex shape of a principal or group id, any version nibble, either case. */
const SUBJECT_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
* Turns a principal's three subject inputs and grant rows into what the no-escalation check
* covers (R-017, R-032, {@link MACHINE_PRINCIPAL_ESCALATION_CHECKED_ROUTES}). Pure: no clock, no
* I/O, and neither input is changed. It returns what to check and gives no verdict: the caller's
* permissions and `canWrite` are async and live in core-api. Caller: WS-5's check in core-api, on
* the five checked routes.
*
* `grants` may hold any grant rows of the org; the function keeps a row when it is active
* (`revokedAt` is null) and its subject is one of the principal's:
*
* - `principal`: `subjectId` equals `principalId`, ignoring case;
* - `group`: `subjectId` equals one of `groupIds`, ignoring case;
* - `role`: `subjectId` equals one of `roleSlugs`, byte for byte.
*
* The kind decides the comparison: a `role` row whose `subjectId` is the principal's uuid, or a
* `principal` row naming one of the group ids, is not the principal's. A revoked row confers
* nothing and is dropped. Ignoring case on the two uuid kinds keeps a row whose id differs in case
* only, so the check covers more and never less.
*
* @param subjects the principal, its flattened groups and every role it holds.
* @param grants grant rows of the org; the plane may pre-filter by subject.
* @returns the slugs of part 1 and the grants of part 2. Empty arrays for a principal with no role
*   and no matching grant.
* @throws a `TypeError` naming the member when `principalId` or a group id is not a uuid, or when a
*   role slug is not a non-empty string, so a malformed input fails the route closed and never
*   narrows the check.
*/
function machinePrincipalEscalationScope(subjects, grants) {
	if (!SUBJECT_UUID.test(subjects.principalId)) throw new TypeError("machinePrincipalEscalationScope: principalId is not a uuid");
	for (const groupId of subjects.groupIds) if (!SUBJECT_UUID.test(groupId)) throw new TypeError(`machinePrincipalEscalationScope: groupIds holds ${JSON.stringify(groupId)}, not a uuid`);
	for (const slug of subjects.roleSlugs) if (typeof slug !== "string" || slug.length === 0) throw new TypeError("machinePrincipalEscalationScope: roleSlugs holds an empty or non-string slug");
	const principalId = subjects.principalId.toLowerCase();
	const groupIds = new Set(subjects.groupIds.map((groupId) => groupId.toLowerCase()));
	const roleSlugs = new Set(subjects.roleSlugs);
	const isSubject = (grant) => {
		switch (grant.subjectKind) {
			case "principal": return grant.subjectId.toLowerCase() === principalId;
			case "group": return groupIds.has(grant.subjectId.toLowerCase());
			case "role": return roleSlugs.has(grant.subjectId);
		}
	};
	return Object.freeze({
		roleSlugs: Object.freeze([...roleSlugs].sort()),
		grants: Object.freeze(grants.filter((grant) => grant.revokedAt === null && isSubject(grant)))
	});
}
//#endregion
export { ADMIN_MACHINE_ENROLL_TOKENS_PATH_TEMPLATE, ADMIN_MACHINE_PRINCIPALS_PATH, ADMIN_MACHINE_PRINCIPAL_KEYS_PATH_TEMPLATE, ADMIN_MACHINE_PRINCIPAL_KEY_PATH_TEMPLATE, ADMIN_MACHINE_PRINCIPAL_REVOKE_PATH_TEMPLATE, ADMIN_MACHINE_PRINCIPAL_TYPE, CREATE_MACHINE_ENROLL_TOKEN_ROUTE, CREATE_MACHINE_PRINCIPAL_ROUTE, LIST_MACHINE_PRINCIPALS_ROUTE, LIST_MACHINE_PRINCIPAL_KEYS_ROUTE, MACHINE_PRINCIPAL_ESCALATION_CHECKED_ROUTES, MACHINE_PRINCIPAL_MAX_ROLES, MACHINE_PRINCIPAL_REFUSED_ROLE_SLUGS, REGISTER_MACHINE_PRINCIPAL_KEY_ROUTE, REVOKE_MACHINE_PRINCIPAL_KEY_ROUTE, REVOKE_MACHINE_PRINCIPAL_ROUTE, adminMachineEnrollTokensPath, adminMachinePrincipalKeyPath, adminMachinePrincipalKeySchema, adminMachinePrincipalKeysPath, adminMachinePrincipalRevokePath, adminMachinePrincipalSchema, createMachineEnrollTokenResponseSchema, createMachinePrincipalRequestSchema, createMachinePrincipalResponseSchema, listMachinePrincipalKeysResponseSchema, listMachinePrincipalsResponseSchema, machinePrincipalEscalationScope, machinePrincipalStatusSchema, machinePrincipalsRoutes, registerMachinePrincipalKeyRequestSchema, registerMachinePrincipalKeyResponseSchema, revokeMachinePrincipalKeyResponseSchema, revokeMachinePrincipalResponseSchema };

//# sourceMappingURL=machine-principals.js.map